import { resRest } from "@root/lib/utils";

/**
 * Actions for vuex
 * @static
 * @memberOf ''
 * @since 0.1.0
 * @category Lang
 * @param {Array<Object> | Object} gqlDatas
 * @param {Array<Object>} options
 * @returns {Object} vuex actions
 * @example
  import gqls from "@root/graphql/wt.gql";
  // callback 调用
  import { resRest, easyMutate } from "@root/lib/utils";

  import ShortActions from "@root/lib/bbqActions";
  const commonQuerys = [
    {
      method: "querySupplierScanRecord",
      // type: "query",
      // source: "querySupplierScanRecord", // gql相关语句
      // variables: {
      //   input: { recordType: "" },
      //   pageInput: { pageSize: 10, pageNum: 1 }
      // },
      cb: (...params) => {
        console.log("cb cb", ...params);
      }
      // cb: ({ commit, state, rootState }: any, data?: any) => {
      //   // console.log("cb cb", commit, data);
      //   commit("easyMutate", { path: "test-test", data: "permission" });
      // }
    }
  ];

  const shortAction = new ShortActions(gqls, commonQuerys);
  const gqlActions = shortAction.getValues();

  const ac = {
    namespaced: true,
    state: {},
    getters: {},
    actions: {
      ...gqlActions
    },
    mutations: {
      easyMutate
    }
  };
  export default ac;
 */
interface RequestParams {
  method: any;
  cb?: (params?: Object) => {};
  type?: "query" | "mutate";
  variables?: Object;
  source?: Object;
}
class BbqActions {
  gqlDatas: Array<Object> | Object;
  queryOptions: Array<Object>;
  static warnPrefixStr: string;
  constructor(gqls: any, queryOptions?: any) {
    this.gqlDatas = this.fGqlDatas(gqls);
    this.queryOptions = this.fOptions(queryOptions);
  }
  /**
   * TODO: 待优化，动态函数名称的调用方式
   * 注册vuex中的查询或者修改函数
   * type: 非必须，gql有类型判断
   * @param params { method , type = "query", variables = {}, source, cb }
   */
  requestParam(params: RequestParams) {
    const { method, type = "query", variables = {}, source, cb } = params;
    const methodFn = method;
    // Dynamic method function name
    class RequestFn {
      /**
       * async Function
       * @param {Object} store
       * @param {Object} variables
       * @param {Object} reponse
       * @return {Object} { { commit, state }, variables, reponse }
       */

      async [methodFn](...args: any) {
        const { rootState } = args.shift(); // store
        const externalVariables = args[0];

        const isQuery = type === "query";
        try {
          const response = await rootState.gql[isQuery ? "query" : "mutate"]({
            [isQuery ? "query" : "mutation"]: source,
            variables: !isEmptyObj(variables) ? variables : externalVariables
          });
          // TODO: 抽离 result， result: { list: [], total: 0 }
          let result = {};
          if (isQuery) {
            result = getResponseData(response);
          } else {
            result = response;
          }
          if (cb && typeof cb === "function") {
            result = Object.assign(result, cb(...[arguments, response]));
          }
          return result;
        } catch (error) {
          console.error(`[Class BbqActions] ${error}`);
          return null;
        }
      }
    }
    const getResponseData = response => {
      const {
        data: {
          api: { dataList = null, pageInfo = null },
          api = null
        },
        data
      } = response;
      let restData = [];
      if (!dataList && api) {
        restData = api;
      } else {
        restData = resRest(data) || [];
      }
      let result = {};
      // TODO: resRest, 兼容path 错误
      if (pageInfo) {
        result = {
          list: restData,
          data: restData,
          total: api.pageInfo.totalCount || api.pageInfo.total || 0
        };
      } else {
        result = {
          list: restData,
          data: restData,
          total: 0
        };
      }
      return result;
    };
    const isEmptyObj = obj => {
      return Object.keys(obj).length === 0 && obj.constructor === Object;
    };
    const instance = new RequestFn();
    return instance[methodFn];
  }

  /**
   * 导出gql文件中的相关信息，自动注册vuex中的 actions TODO: 待优化
   * 可能的问题：.definitions[0] 获取第一层的相关信息，definitions：解析嵌套的gql多个接口fragment
   * @returns actions
   */
  dealGqlActions(gqlDatas) {
    // validGqls合法的，在gql文件里自定义的接口
    let validGqls = [];
    for (const key in gqlDatas) {
      const value = gqlDatas[key];
      const { kind, loc, definitions } = value;
      const isDiyGql = kind && loc && definitions;
      const firstDefinition = definitions && definitions[0];
      if (isDiyGql) {
        validGqls.push({
          source: value,
          method: key,
          type: (firstDefinition && firstDefinition.operation) || "query"
        });
      }
    }
    // merge 自定义方式
    let gqlDataList = validGqls;
    let commonQueryDataList = this.queryOptions as any;
    const otherActions = gqlDataList.reduce((acc, cur) => {
      const { method: curMethod } = cur;
      // 如果有自定义的在commonQuerys里面，就覆盖自动定义
      let hasMergeObj = commonQueryDataList.find(
        ({ method }) => method === curMethod
      );
      if (hasMergeObj) {
        // 除了source（gql查询），type（改方法查询类型）其余的都覆盖相关
        const { source = "", type = "", ...item } = hasMergeObj;
        Object.assign(cur, item);
      }
      return Object.assign(acc, {
        [cur && cur.method]: this.requestParam(cur)
      });
    }, {});
    return otherActions;
  }

  fGqlDatas(gqlInfos) {
    // 传入的是数组，即导出的各种接口值。传入的是对象，即直接全部从gql文件中导入
    if (this.isArray(gqlInfos)) {
      let gqlObj = {};
      for (let value of gqlInfos) {
        const { definitions } = value;
        const firstDefinition = definitions && definitions[0];
        const firstDefinitionName =
          firstDefinition && firstDefinition.name.value;
        gqlObj[firstDefinitionName] = value;
      }
      gqlInfos = gqlObj;
      return gqlInfos;
    }
    if (this.isObject(gqlInfos)) {
      return gqlInfos;
    }
    this.warn("Params gql data type should be Array or Obejct");
  }
  fOptions(options) {
    if (!(options instanceof Array)) {
      this.warn("Params options data type should be Array");
      return [];
    }
    return options;
  }
  /** 获取处理之后的值，暴露给外部的接口 */
  getValues() {
    return this.dealGqlActions(this.gqlDatas);
  }
  warn(message: String, error?: Error) {
    if (typeof console != undefined) {
      console.warn(`[Class BbqActions] ${message}`);
      if (error) {
        console.warn(error.stack);
      }
    }
  }
  isArray = el => Array.isArray(el);
  isObject = el => Object.prototype.toString.call(el) === "[object Object]";
}

/**

 */
export default BbqActions;
